import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import ReviewEditForm from '../../forms/reviews/ReviewEditForm';
import AdminReviewService from '../../services/AdminReviewService';
import '../../styles/AdminModal.css';

const ReviewEditModal = ({ isOpen, onClose, onSuccess, review, boats }) => {
  const [formData, setFormData] = useState({
    reviewerName: '',
    email: '',
    comment: '',
    rating: 5,
    boat_id: '',
    isApproved: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const isFormValid =
    formData.reviewerName.trim() !== '' &&
    formData.email.trim() !== '' &&
    formData.comment.trim() !== '' &&
    formData.boat_id !== '';

  const hasChanges = () => {
    if (!review) return false;
    
    return (
      formData.reviewerName !== review.reviewer_name ||
      formData.email !== review.email ||
      formData.comment !== review.comment ||
      formData.rating !== review.rating ||
      formData.boat_id !== (review.boat?.id || '') ||
      formData.isApproved !== review.is_approved
    );
  };

  useEffect(() => {
    if (review) {
      setFormData({
        reviewerName: review.reviewer_name || '',
        email: review.email || '',
        comment: review.comment || '',
        rating: review.rating || 5,
        boat_id: review.boat?.id || '',
        isApproved: review.is_approved || false
      });
      setErrors({});
    }
  }, [review]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingChange = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating: rating
    }));
  };

  const handleStatusChange = (e) => {
    const status = e.target.value;
    setFormData(prev => ({
      ...prev,
      isApproved: status === 'approved'
    }));
  };

  const handleSave = async () => {
    // Validación del frontend
    const validationErrors = {};
    
    if (!formData.reviewerName?.trim()) {
      validationErrors.reviewerName = 'Reviewer name is required';
    }
    
    if (!formData.email?.trim()) {
      validationErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      validationErrors.email = 'Invalid email format';
    }
    
    if (!formData.comment?.trim()) {
      validationErrors.comment = 'Comment is required';
    } else if (formData.comment.length > 300) {
      validationErrors.comment = 'Comment cannot exceed 300 characters';
    }
    
    if (!formData.boat_id) {
      validationErrors.boat_id = 'Boat is required';
    }
    
    const rating = formData.rating || 5;
    if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
      validationErrors.rating = 'Rating must be between 1 and 5';
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    try {
      const dataToSave = {
        reviewer_name: formData.reviewerName.trim(),
        email: formData.email.trim(),
        comment: formData.comment.trim(),
        rating: formData.rating,
        boat: formData.boat_id,
        is_approved: formData.isApproved
      };

      await AdminReviewService.updateReview(review.id, dataToSave);
      await onSuccess();
    } catch (error) {
      console.error('Error updating review:', error);
      
      // Handle specific backend validation errors
      if (error.response && error.response.data && error.response.data.error) {
        if (typeof error.response.data.error === 'object') {
          // Multiple field errors
          setErrors(error.response.data.error);
        } else {
          // Single error message
          setErrors({ submit: error.response.data.error });
        }
      } else if (error.message) {
        // Error from service
        setErrors({ submit: error.message });
      } else {
        setErrors({ submit: 'Error updating review. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="admin__modal">
      <div className="admin__modal-content">
        <div className="admin__modal-intro">
          <h2>Edit Review</h2>
          <button className="admin__modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>
        {errors.submit && <div className="error-message">{errors.submit}</div>}
        <div className="admin__modal-body">
          <ReviewEditForm
            formData={formData}
            errors={errors}
            handleChange={handleChange}
            handleRatingChange={handleRatingChange}
            handleStatusChange={handleStatusChange}
            boats={boats}
          />
        </div>
        <div className="admin__modal-buttons">
          <button 
            className="button secondary" 
            onClick={onClose}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Cancel'}
          </button>
          <button 
            className="button"
            onClick={handleSave}
            disabled={loading || !isFormValid || !hasChanges()}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewEditModal; 